import React from "react";
import { Card, CardBody, Button } from "@heroui/react";
import { Link } from "react-router-dom";

import { Icon } from "@iconify/react";
import { Helmet } from 'react-helmet-async';
import { motion } from "framer-motion";
import { useLocation } from 'react-router-dom';
import { BASE_URL } from "../services/config";

const giftExamples = [
  {
    title: "Подарочный набор для сотрудников",
    image: "/assets/images/gift-corporate.jpg",
    desc: "Индивидуальный брендинг, фирменная упаковка, подбор ароматов под стиль компании."
  },
  {
    title: "VIP-набор для партнеров",
    image: "/assets/images/gift-vip.jpg",
    desc: "Эксклюзивные ароматы, премиальные материалы, персонализированные открытки."
  },
  {
    title: "Новогодние подарки",
    image: "/assets/images/gift-newyear.jpg",
    desc: "Сезонные коллекции, тематическая упаковка, доставка по всей России."
  }
];

export const PartnersGiftsPage: React.FC = () => {
  const location = useLocation();
  // Анимации для секций и карточек
  const sectionVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } }
  };
  const cardsContainer = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.18 } }
  };
  const cardItem = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } }
  };

  return (
    <>
      <Helmet>
        <title>Корпоративные подарки — SAFONOVA</title>
        <meta name="description" content="Премиальные подарочные наборы с индивидуальным брендингом для сотрудников, клиентов и партнёров." />
        <link rel="canonical" href={`${BASE_URL || ''}${location?.pathname || '/partners-gifts'}`} />
        <meta property="og:title" content="Корпоративные подарки — SAFONOVA" />
        <meta property="og:description" content="Премиальные подарочные наборы с индивидуальным брендингом для сотрудников, клиентов и партнёров." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`${BASE_URL || ''}${location?.pathname || '/partners-gifts'}`} />
        <meta property="og:image" content={`${BASE_URL || ''}/og-default.png`} />
      </Helmet>
      {/* Hero Section */}
      <motion.section
        className="relative flex items-center py-32 md:py-40 bg-gradient-to-br from-primary/10 to-background"
        variants={sectionVariants}
        initial="hidden"
        animate="visible"
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="container mx-auto px-6 flex flex-col items-start">
          <motion.div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-light mb-6 text-foreground">
              Корпоративные <span className="font-semibold">подарки</span>
            </h1>
            <p className="text-lg md:text-xl text-foreground/80 mb-8 max-w-xl">
              Премиальные подарочные наборы с индивидуальным брендингом для ваших сотрудников, клиентов и партнеров.
            </p>
            <Button
              as={Link}
              to="/contacts"
              color="primary"
              size="lg"
              className="font-medium"
              endContent={<Icon icon="lucide:mail" width={18} />}
            >
              Оставить заявку
            </Button>
          </motion.div>
        </div>
      </motion.section>

      {/* Advantages Section */}
      <motion.section
        className="py-20 bg-background"
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
      >
        <div className="container mx-auto px-6">
          <motion.div
            className="grid md:grid-cols-3 gap-8 mb-16"
            variants={cardsContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <motion.div variants={cardItem}>
              <Card className="border border-divider bg-content2/60" disableRipple>
                <CardBody className="p-6 flex flex-col items-center text-center">
                  <Icon icon="lucide:badge-check" width={36} className="mb-4 text-primary" />
                  <h3 className="text-lg font-medium mb-2">Индивидуальный брендинг</h3>
                  <p className="text-foreground/70">Логотип вашей компании, фирменные цвета, персонализированные открытки и упаковка.</p>
                </CardBody>
              </Card>
            </motion.div>
            <motion.div variants={cardItem}>
              <Card className="border border-divider bg-content2/60" disableRipple>
                <CardBody className="p-6 flex flex-col items-center text-center">
                  <Icon icon="lucide:leaf" width={36} className="mb-4 text-primary" />
                  <h3 className="text-lg font-medium mb-2">Экологичные материалы</h3>
                  <p className="text-foreground/70">Используем только натуральные воски, масла и упаковку из перерабатываемых материалов.</p>
                </CardBody>
              </Card>
            </motion.div>
            <motion.div variants={cardItem}>
              <Card className="border border-divider bg-content2/60" disableRipple>
                <CardBody className="p-6 flex flex-col items-center text-center">
                  <Icon icon="lucide:truck" width={36} className="mb-4 text-primary" />
                  <h3 className="text-lg font-medium mb-2">Доставка по всей России</h3>
                  <p className="text-foreground/70">Оперативная доставка корпоративных заказов в любой регион страны.</p>
                </CardBody>
              </Card>
            </motion.div>
          </motion.div>

          <motion.div
            className="text-center mb-12"
            variants={sectionVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <h2 className="text-3xl md:text-4xl font-light mb-4">Примеры <span className="font-medium">подарочных наборов</span></h2>
            <p className="text-foreground/70 max-w-2xl mx-auto">
              Мы подберём идеальное решение под ваш бренд, бюджет и пожелания. Вот несколько реализованных кейсов:
            </p>
          </motion.div>
          <motion.div
            className="grid md:grid-cols-3 gap-8"
            variants={cardsContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            {giftExamples.map((gift, i) => (
              <motion.div key={i} variants={cardItem}>
                <Card className="border border-divider bg-content2/80" disableRipple>
                  <CardBody className="p-6 flex flex-col items-center text-center">
                    <img src={gift.image} alt={gift.title} className="mb-4 rounded-lg shadow w-full h-48 object-cover" />
                    <h4 className="text-lg font-medium mb-2">{gift.title}</h4>
                    <p className="text-foreground/70">{gift.desc}</p>
                  </CardBody>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section>

      {/* How to order section */}
      <motion.section
        className="py-20 bg-content2"
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
      >
        <div className="container mx-auto px-6">
          <motion.div
            className="text-center mb-12"
            variants={sectionVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <h2 className="text-3xl md:text-4xl font-light mb-4">Как <span className="font-medium">заказать</span></h2>
            <p className="text-foreground/70 max-w-2xl mx-auto">
              Мы сопровождаем вас на каждом этапе — от идеи до доставки готовых подарков.
            </p>
          </motion.div>
          <motion.div
            className="grid md:grid-cols-4 gap-8"
            variants={cardsContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <motion.div variants={cardItem}>
              <Card className="border border-divider" disableRipple>
                <CardBody className="p-6 flex flex-col items-center text-center">
                  <Icon icon="lucide:message-circle" width={32} className="mb-3 text-primary" />
                  <h4 className="font-medium mb-1">Заявка</h4>
                  <p className="text-foreground/70 text-sm">Оставьте заявку или свяжитесь с нами для консультации.</p>
                </CardBody>
              </Card>
            </motion.div>
            <motion.div variants={cardItem}>
              <Card className="border border-divider" disableRipple>
                <CardBody className="p-6 flex flex-col items-center text-center">
                  <Icon icon="lucide:palette" width={32} className="mb-3 text-primary" />
                  <h4 className="font-medium mb-1">Согласование</h4>
                  <p className="text-foreground/70 text-sm">Обсудим детали, предложим варианты наполнения, упаковки и брендирования.</p>
                </CardBody>
              </Card>
            </motion.div>
            <motion.div variants={cardItem}>
              <Card className="border border-divider" disableRipple>
                <CardBody className="p-6 flex flex-col items-center text-center">
                  <Icon icon="lucide:factory" width={32} className="mb-3 text-primary" />
                  <h4 className="font-medium mb-1">Производство</h4>
                  <p className="text-foreground/70 text-sm">Изготовим подарки с учётом всех пожеланий и стандартов качества.</p>
                </CardBody>
              </Card>
            </motion.div>
            <motion.div variants={cardItem}>
              <Card className="border border-divider" disableRipple>
                <CardBody className="p-6 flex flex-col items-center text-center">
                  <Icon icon="lucide:truck" width={32} className="mb-3 text-primary" />
                  <h4 className="font-medium mb-1">Доставка</h4>
                  <p className="text-foreground/70 text-sm">Оперативно доставим заказ в ваш офис или по адресам получателей.</p>
                </CardBody>
              </Card>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>

      {/* Contact form section (placeholder) */}
      <motion.section
        className="py-20 bg-background"
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
      >
        <div className="container mx-auto px-6 max-w-2xl">
          <motion.div
            className="text-center mb-8"
            variants={sectionVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <h2 className="text-3xl md:text-4xl font-light mb-4">Оставить <span className="font-medium">заявку</span></h2>
            <p className="text-foreground/70">
              Заполните форму, и мы свяжемся с вами для обсуждения деталей и подготовки индивидуального предложения.
            </p>
          </motion.div>
          {/* Здесь может быть форма обратной связи или импорт готового компонента */}
          <motion.div
            className="bg-content2/60 rounded-xl p-8 text-center text-foreground/60"
            variants={cardItem}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            [Форма обратной связи будет здесь]
          </motion.div>
        </div>
      </motion.section>
    </>
  );
};

export default PartnersGiftsPage;
